/** Base URL des scripts PHP push (sans slash final). */
export function getPushApiBase(): string {
  const fromEnv = (import.meta.env.VITE_PUSH_API_BASE || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  const base = import.meta.env.BASE_URL || '/';
  const normalized = base.endsWith('/') ? base : `${base}/`;
  return `${normalized}gopaas-api/push`.replace(/\/$/, '');
}

export function getVapidPublicKey(): string {
  return sanitizeVapidKeyBase64Url(import.meta.env.VITE_VAPID_PUBLIC_KEY || '');
}

/** Retire espaces / retours ligne / BOM, puis tout caractere hors base64 URL (+ padding =). */
function normalizeVapidPublicKeyString(raw: string): string {
  return String(raw)
    .replace(/^\uFEFF/, '')
    .replace(/\s+/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
}

/** Ne garde que les symboles base64 URL + padding (=). Le tiret - doit etre inclus (sinon cle tronquee). */
function sanitizeVapidKeyBase64Url(s: string): string {
  return normalizeVapidPublicKeyString(s).replace(/[^-A-Za-z0-9_=]/g, '');
}

export function isWebPushConfigured(): boolean {
  return getVapidPublicKey().length > 0;
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const VAPID_P256_UNCOMPRESSED_LENGTH = 65;
const VAPID_UNCOMPRESSED_PREFIX = 0x04;

/**
 * Decode la cle publique VAPID (base64 URL) pour `applicationServerKey`.
 * Web Push exige une cle P-256 non compressee : 65 octets, premier octet 0x04.
 */
export function decodeVapidApplicationServerKey(base64Url: string): Uint8Array {
  const cleaned = sanitizeVapidKeyBase64Url(base64Url);
  if (!cleaned) {
    throw new Error('Cle VAPID publique vide (VITE_VAPID_PUBLIC_KEY).');
  }
  let bytes: Uint8Array;
  try {
    bytes = urlBase64ToUint8Array(cleaned);
  } catch {
    throw new Error(
      'Cle VAPID publique illisible (base64 URL). Verifiez .env : une seule ligne, sans guillemets autour de la valeur si votre outil les duplique. Regenerer : npx web-push generate-vapid-keys'
    );
  }
  if (bytes.length !== VAPID_P256_UNCOMPRESSED_LENGTH) {
    throw new Error(
      `Cle VAPID : ${bytes.length} octets apres decodage (attendu ${VAPID_P256_UNCOMPRESSED_LENGTH}). ` +
        'Souvent cle tronquee, mauvaise cle (ex. privee), ou copie incomplete. Utiliser la ligne « Public Key » de `npx web-push generate-vapid-keys`, identique a vapid_public dans config.local.php.'
    );
  }
  if (bytes[0] !== VAPID_UNCOMPRESSED_PREFIX) {
    throw new Error(
      'Cle VAPID : format inattendu (le premier octet doit etre 0x04). Utilisez la cle publique generee par web-push (pas une cle PEM).'
    );
  }
  return bytes;
}

/**
 * Verifie que les octets forment bien une cle publique ECDH P-256 brute (format Web Push / VAPID).
 * Evite l erreur navigateur opaque « applicationServerKey is not valid ».
 */
async function assertP256UncompressedPublicKey(bytes: Uint8Array): Promise<void> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return;
  const copy = new Uint8Array(bytes);
  try {
    await subtle.importKey('raw', copy, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  } catch {
    throw new Error(
      'Cle VAPID : Web Crypto refuse cette cle (point P-256 invalide ou cle tronquee / melangee avec une autre paire). ' +
        'Executer `npx web-push generate-vapid-keys`, copier la ligne Public Key complete dans VITE_VAPID_PUBLIC_KEY, ' +
        'la meme dans vapid_public (PHP), la Private Key uniquement dans vapid_private, puis `npm run build` et redeployer.'
    );
  }
}

function registerSecretHeader(): Record<string, string> {
  const secret = (import.meta.env.VITE_PUSH_REGISTER_SECRET || '').trim();
  return secret ? { 'X-Tifosi-Push-Register': secret } : {};
}

export async function postSubscription(
  subscription: PushSubscriptionJSON,
  tifosiCle?: string
): Promise<void> {
  const base = getPushApiBase();
  const res = await fetch(`${base}/subscribe.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...registerSecretHeader(),
    },
    body: JSON.stringify({
      subscription,
      tifosiCle: tifosiCle || undefined,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg || `HTTP ${res.status}`);
  }
}

export async function postUnsubscribe(endpoint: string): Promise<void> {
  const base = getPushApiBase();
  const res = await fetch(`${base}/unsubscribe.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...registerSecretHeader(),
    },
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
}

export async function subscribeWithExistingRegistration(
  registration: ServiceWorkerRegistration,
  tifosiCle?: string
): Promise<PushSubscription> {
  const key = getVapidPublicKey();
  if (!key) throw new Error('VITE_VAPID_PUBLIC_KEY manquant');

  let applicationServerKey: Uint8Array;
  try {
    applicationServerKey = decodeVapidApplicationServerKey(key);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(detail);
  }

  await assertP256UncompressedPublicKey(applicationServerKey);

  let sub: PushSubscription;
  try {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    const hint =
      /applicationServerKey|not valid/i.test(detail)
        ? ' Regenerer une paire avec `npx web-push generate-vapid-keys`, mettre la Public Key dans VITE_VAPID_PUBLIC_KEY (rebuild) et la meme valeur dans vapid_public du PHP ; ne pas melanger avec une autre cle privee.'
        : '';
    throw new Error(
      `Etape navigateur (pushManager.subscribe) impossible : ${detail}.${hint} ` +
        'Tant que cette etape echoue, aucun POST vers subscribe.php n est envoye. HTTPS obligatoire.'
    );
  }

  try {
    await postSubscription(sub.toJSON(), tifosiCle);
  } catch (err) {
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    const base = getPushApiBase();
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Enregistrement serveur echoue (${base}/subscribe.php) : ${detail}. ` +
        'Le navigateur a ete desabonne pour rester coherent.'
    );
  }

  return sub;
}

export async function unsubscribePush(registration: ServiceWorkerRegistration): Promise<void> {
  const sub = await registration.pushManager.getSubscription();
  if (!sub) return;
  const json = sub.toJSON();
  const endpoint = json.endpoint;
  if (endpoint) {
    try {
      await postUnsubscribe(endpoint);
    } catch {
      /* serveur indisponible : on désabonne quand même localement */
    }
  }
  await sub.unsubscribe();
}
